import { CAAS_INDEX_SUFFIX, DISABLE_ADS } from "@src/app/constants";
import {
  GA4_REF_CODE_CONTENTBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_C,
  GA4_REF_CODE_CONTENTBLOCK_J,
  GA5_REF_CODE_CONTENTHUB_BLOCK,
} from "@src/app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  PREMUTIVE_CONFIG,
  VWO_CONFIG,
} from "@src/pubs/common/config";

import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigPermutive,
  ConfigRss,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
  ConfigVWO,
} from "../types/config";
import ContentListCarousel from "./components/ContentListCarousel";
import {
  ButtonWhiteArrowRight,
  chevronLeftIcon,
  chevronRightIcon,
  ctaIcon,
  VideoGalleryArrowLeft,
  VideoGalleryArrowRight,
} from "./hbsIcons";
import logoPath from "./logo.svg?url";
import newsletterBg from "./newslettersignupbg.jpg";

export const configSite: ConfigSite = {
  name: "Harper's Bazaar Singapore",
  address:
    import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.harpersbazaar.com.sg", // without trailing slash
  tagline: "Fashion, Beauty, Travel, Parties & Culture",
  description:
    "Sophisticated, elegant and provocative: the latest in fashion & beauty, what to wear and buy now, celebrity style, travel, art & culture news, reviews and videos",
  titleSeparator: "|",
  logoPath,
  twitterSite: "@harpersbazaarsg",
  pageShare: {
    media: [
      {
        name: "Facebook",
      },
      {
        name: "Telegram",
      },
      {
        name: "X",
      },
      {
        name: "Whatsapp",
      },
    ],
  },
  meta: [
    { name: "msapplication-TileColor", content: "#000000" },
    { name: "theme-color", content: "#000000" },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#b30010" },
  ],
};

export { configHeader } from "./config/configHeader";

export const configCaas: ConfigCaas = {
  index: `harpersbazaar${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:harpersbazaar-keywords@sph.com.sg,2023:",
  cuePublicationName: "harpersbazaar",
};

export const configGTM: ConfigGTM = {
  container: "GTM-T9QSQQW", //HBS
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/hbs",
  debounce: true,
  debounceDelay: 300,
  paragraphMinLength: 100,
};

export const configLotame: ConfigLotame = {
  active: !DISABLE_ADS,
  clientId: "12374",
};

export const configPermutive: ConfigPermutive = {
  ...PREMUTIVE_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1768898",
  geniuslinkId: "394416",
};

export const configVWO: ConfigVWO = {
  ...VWO_CONFIG,
  active: true,
};

export const configBrightcove: ConfigBrightcove = {
  ...BRIGHTCOVE_CONFIG,
};

export const configSingleStory: ConfigSingleStory = {
  authorProfile: true,
  byline: {
    moveToShare: true,
    defaultCredit: "Harper’s Bazaar Singapore Team",
  },
  prefixForDateLine: "published ",
  configShareArticle: {
    label: "Share this article",
    divider: {
      top: true,
    },
  },
  storylineImage: {
    // HBS image links not used because import data misunderstanding
    hyperlink: false,
    // HBS does not allowing editor selection because imported images miss crop selection
    fixedCropSelection: "original",
  },
  displayTags: {
    displayBottomTags: true,
    displayTagHeading: true,
  },
  aiSummary: {
    enabled: true,
    titleAtHeader: true,
  },
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    showInlineAds: true,
  },
  configMasthead: {
    heading: {
      display: true,
    },
  },
  configBodyContent: {
    display: false,
  },
  configShareButton: {
    display: false,
  },
  configEditorsNote: {
    display: true,
  },
  configCallQoute: {
    display: false,
  },
  configFeaturedSection: {
    display: true,
  },
  configDeals: {
    display: false,
  },
  configTopStories: {
    display: true,
    firstItemAnchorBlock: true,
  },
  socialVideo: {
    display: true,
  },
  storiesPerPage: {
    topStories: 7,
    totalStories: 13,
  },
  videoGalleryLandscape: {
    display: true,
    heading: {
      title: true,
      hasArrows: true,
      leftIcon: VideoGalleryArrowLeft,
      rightIcon: VideoGalleryArrowRight,
    },
    strip: {
      dividerVariant: "primary",
    },
    viewMore: {
      text: "VIEW ALL VIDEOS",
      href: "/videos",
      icon: ctaIcon,
    },
  },
  layout: {
    stories: {
      elements: [
        {
          Component: ContentListCarousel,
          name: "contentListBlock",
          key: "topic-0",
          fallback: false,
        },
        { name: "videoplaylist" },
        { name: "contentBlockF", limit: 7 },
        { name: "adsMidContent" },
      ],
    },
  },
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 20,
  subAnchorLimit: 4,
  landscapeIndexes: [0, 3],
  mainContentBlockDisplay: "grid",
  mainContentBlockMode: "portrait",
  portraitImageWidth: 300,
  landscapeImageWidth: 630,
  errorLayoutMode: "portrait",
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 4,
  latestStoriesLimit: 8,
  card: {
    anchorblock: {
      defaultVariant: "anchorblock-stacked-square3026",
      mobileUp: {
        defaultVariant: "anchorblock-stacked-mobile-up",
      },
    },
    subAnchor: {
      defaultVariant: "card-list-inverted-original",
      mobileUp: {
        defaultVariant: "card-stacked-portrait-mobile-up",
        variantsByIndex: [
          [0, "card-stacked-landscape-mobile-up"],
          [3, "card-stacked-landscape-mobile-up"],
        ],
      },
    },
    latestArticle: {
      defaultVariant: "card-list-original",
      mobileUp: {
        defaultVariant: "card-stacked-portrait-mobile-up",
      },
    },
  },
};

export const configTag: ConfigTag = {
  subHeading: "Tag",
  topStoriesLimit: 5,
  listedStoriesLimit: 13,
};

export const configAmazonAffiliates = {};

export const configTaboola: ConfigTaboola = {
  enabled: true,
  publisherId: "sphmedia-harpersbazaar",
};

export const configSearch: ConfigSearch = {};

export const configExploreMore: ConfigExploreMore = {
  heading: "EXPLORE OTHER TOPICS",
};

export const configVideoLanding: ConfigVideoLanding = {
  videoPlaylistFillUpSize: 9,
  videoPath: "/videos",
};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {};

export const configHome: ConfigHome = {
  headingDivider: true,
  viewMore: { icon: ctaIcon },
  latestArticle: {
    landscapeIndexes: [0, 3],
  },
  newsletter: {
    containerImageBg: newsletterBg,
    newsletterTitle:
      "Your front row access to the latest in fashion, beauty and celebrities.",
    newsletterBody:
      "Sign up for the Harper's BAZAAR newsletter. Delivered weekly to your inbox.",
  },
  magazineListing: {
    ctaIcon: ButtonWhiteArrowRight,
  },
  layoutGrid: {
    elements: [
      {
        name: "anchorBlock",
        dataSource: {
          type: "list",
          key: "anchorblock",
          fallback: "latest",
          limit: 1,
        },
      },
      {
        name: "latestArticle",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 4,
        },
      },
      {
        name: "contentBlockD",
        dataSource: {
          type: "list",
          key: "trending",
          fallback: "trending",
          title: "Trending",
          refCode: "trending",
          limit: 3,
        },
        alternateBg: true,
      },
      { name: "adUnit" },
      {
        name: "contentBlockB",
        dataSource: {
          type: "tag",
          key: "topic-0",
          refCode: "block-b",
          limit: 3,
          min: 3,
        },
      },
      {
        name: "videoGalleryLandscape",
        dataSource: {
          type: "list",
          key: "videoplaylist",
          refCode: "videoplaylist",
          fallback: "videoplaylist",
          limit: 4,
        },
      },
      {
        name: "contentBlockA",
        dataSource: {
          type: "section",
          key: "fashion",
          refCode: "block-a",
          limit: 5,
        },
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "beauty",
          refCode: "block-f",
          limit: 3,
        },
        alternateBg: true,
      },
      { name: "magazineListing" },
      { name: "newsletter" },
    ],
  },
  contentBlockCarousel: {
    leftIcon: chevronLeftIcon,
    rightIcon: chevronRightIcon,
  },
  videoGalleryLandscape: {
    heading: {
      hasArrows: true,
    },
    strip: {
      dividerVariant: "primary",
    },
    viewMore: {
      text: "VIEW ALL VIDEOS",
    },
  },
  trendingStories: { publisher: "hpz" },
};

export const configNewsletter: ConfigNewsletter = {
  image: newsletterBg,
  title: `Your front row access to the latest in fashion, beauty and celebrities.`,
  body: `Sign up for the Harper's BAZAAR newsletter. Delivered weekly to your inbox.`,
  subscriptionMessage: {
    title: `Thank you for subscribing!`,
    body: `You’re on the list! Consider this your front-row pass to the latest trends, celebrity news, and industry insights—delivered directly to your inbox. \n\nLook out for your first newsletter hitting your inbox soon!`,
  },
};

export const configTeaser: ConfigTeaser = {};

export const configTrackingCode: ConfigTrackingCode = {
  contenthub: {
    topstories: {
      contentBlockB: GA4_REF_CODE_CONTENTBLOCK_B,
      contentBlockF: GA4_REF_CODE_CONTENTBLOCK_J,
    },
    featured: GA4_REF_CODE_CONTENTBLOCK_C,
    listing: GA5_REF_CODE_CONTENTHUB_BLOCK,
  },
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {};

export const configError: ConfigError = {};

export const configAuthorProfile: ConfigAuthorProfile = {
  hideAds: true,
  hideDivider: true,
  showReadMore: true,
  teaserLimit: 14,
  listingLayout: {
    mainContentBlockDisplay: "grid",
    mainContentBlockMode: "portrait",
  },
};

export const configAboutUs: ConfigAboutUs = {
  profileBlock: {
    heading: "MEET THE TEAM",
  },
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {};

export const configAffiliateShopping: ConfigAffiliateShopping = {
  withDivider: true,
  layoutGrid: {
    elements: [
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-7-grid",
          limit: 7,
          config: {
            cta: {
              name: "SHOP MORE IN STORY",
            },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-stacked",
                mobileUp: {
                  defaultVariant: "anchorblock-stacked-mobile-up",
                },
              },
            },
            moreStories: {
              ads: {
                sidebar: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  variantsByIndex: [[0, "card-stacked-landscape-mobile-up"]],
                  defaultVariant: "card-stacked-portrait-mobile-up",
                },
              },
              layout: {
                mobileUp: "grid",
              },
            },
            productMobileLayout: "list",
          },
        },
      },
      {
        name: "products",
        dataSource: {
          types: ["list"],
          key: "products-6-grid",
          limit: 6,
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section"],
          key: "stories-4",
          limit: 4,
          config: {
            backgroundColor: "#000",
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-stacked",
                mobileUp: {
                  defaultVariant: "anchorblock-stacked-mobile-up",
                },
              },
            },
            moreStories: {
              ads: {
                sidebar: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: {
                  variantsByIndex: [[0, "card-stacked-landscape-mobile-up"]],
                  defaultVariant: "card-stacked-portrait-mobile-up",
                },
              },
              layout: {
                mobileUp: "grid",
              },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-4-grid",
          limit: 4,
          config: {
            cta: {
              name: "SHOP MORE IN STORY",
            },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-stacked",
                mobileUp: {
                  defaultVariant: "anchorblock-stacked-mobile-up",
                },
              },
            },
            productMobileLayout: "grid",
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-3",
          limit: 3,
          config: {
            backgroundColor: "#000",
            moreStories: {
              isLandscape: true,
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant: "card-stacked-portrait-mobile-up",
                },
              },
              layout: {
                mobileUp: "stacked",
              },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-12",
          limit: 12,
          config: {
            cta: {
              name: "View all deals",
            },
            moreStories: {
              isLandscape: true,
              hasCta: true,
              ads: {
                sidebar: false,
                leaderboard: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-landscape",
                mobileUp: {
                  defaultVariant: "card-stacked-portrait-mobile-up",
                },
              },
              layout: {
                mobileUp: "stacked",
              },
            },
          },
        },
      },
    ],
  },
  viewMore: { icon: ctaIcon },
};

export const configRss: ConfigRss = [];

export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
  breakpoints: [
    {
      containerMinWidth: 1000,
      visibleItems: 5,
    },
    {
      containerMinWidth: 600,
      visibleItems: 3,
    },
    {
      containerMinWidth: 420,
      visibleItems: 2,
    },
  ],
};
